import { LINE_GAP_THRESHOLD_MS, LINE_SAMPLING } from '@/features/time-series-chart/config/chartConfig';
import type { DiagramSeriesOption } from '@/features/time-series-chart/types/DiagramSeries.types';
import type { TimeSeriesChartContent } from '@/features/time-series-chart/types/TimeSeriesChartModel.types';
import type { TimeWindowMicros } from '@/features/time-series-chart/types/TimeWindow.types';
import type { DataSeries } from '@/generated/model';
import type { TimeRangeLane, TimeSeries } from '@/shared/charts';

import { getDataSeriesPoints, getDataSeriesRanges } from '../data-series/dataSeriesMappers';

type MapDataSeriesToChartContentParams = {
  metadata: DiagramSeriesOption[];
  selectedSeriesIds: string[];
  dataSeries: DataSeries[];
  getSeriesColor: (seriesId: string) => string;
  timeWindow: TimeWindowMicros;
};

function mapNumericSeries(
  option: DiagramSeriesOption,
  dataSeries: DataSeries | undefined,
  color: string,
  timeWindow: TimeWindowMicros
): TimeSeries {
  return {
    id: option.id,
    label: option.label,
    unit: option.unit,
    color,
    sampling: LINE_SAMPLING,
    gapThresholdMs: LINE_GAP_THRESHOLD_MS,
    axisGroupId: option.channelType,
    points: dataSeries ? getDataSeriesPoints(dataSeries, timeWindow) : [],
  };
}

function mapStateLane(
  option: DiagramSeriesOption,
  dataSeries: DataSeries | undefined,
  color: string,
  timeWindow: TimeWindowMicros
): TimeRangeLane {
  return {
    id: option.id,
    label: option.label,
    color,
    ranges: dataSeries ? getDataSeriesRanges(dataSeries, timeWindow) : [],
  };
}

/**
 * Lines come from the selected numeric series and range-track lanes from the selected state series,
 * so a checkbox shows or hides a state exactly like it does a measurement channel. This part does
 * not depend on the highlight, so a highlight toggle can reuse it.
 */
export function mapDataSeriesToChartContent({
  metadata,
  selectedSeriesIds,
  dataSeries,
  getSeriesColor,
  timeWindow,
}: MapDataSeriesToChartContentParams): TimeSeriesChartContent {
  const selectedIds = new Set(selectedSeriesIds);
  const seriesById = new Map(dataSeries.map((series) => [series.id, series]));

  const series = metadata
    .filter((option) => option.valueType === 'number' && selectedIds.has(option.id))
    .map((option) => mapNumericSeries(option, seriesById.get(option.id), getSeriesColor(option.id), timeWindow));

  const lanes = metadata
    .filter((option) => option.valueType === 'boolean' && selectedIds.has(option.id))
    .map((option) => mapStateLane(option, seriesById.get(option.id), getSeriesColor(option.id), timeWindow));

  return {
    series,
    ...(lanes.length > 0 ? { rangeTrack: { lanes } } : {}),
  };
}
